import React, { useState } from 'react'
// libs
import { useMutation } from '@apollo/client/react'
import { InjectedFormProps, reduxForm } from 'redux-form'
// hooks
import { useModalData } from 'hooks/index'
// components
import { Button, BUTTON_LAYOUTS, CREATE_BUTTON_STYLE, Modal } from 'components/UI'
import { Header } from 'components/Modals/ScaleOptionModal/Create'
import { CloseIcon } from 'components/UI/Icons'
import { ReduxInput } from 'components/FormElements/ReduxForm'
import BackupCodesDisplay from '../TwoFactorSetupModal/components/BackupCodesDisplay'
// constants
import { Modals } from 'constants/modals'
import { Forms } from 'constants/forms'
import { PAPER_PROPS } from './constants'
// graphql
import { AUTH_TWO_FACTOR_REGENERATE_CODES_MUTATION } from 'graphql/auth/twoFactor/mutations'
// views
import { ButtonGroup, FormContainer, ScrollableContent, SuccessContainer, SuccessTitle } from './views'
// //types
import { IAuthTwoFactorRegenerateBackupCodesResponse } from 'types/graphql'
// utils
import { required } from 'utils/validators'
import { handleError } from 'utils/errors/handlers'

type FormData = {
  code: string
}

type OwnProps = {
  onSuccess: (codes: string[]) => void
}

const TwoFactorRegenerateCodesForm: React.FC<InjectedFormProps<FormData, OwnProps> & OwnProps> = ({
  handleSubmit,
  reset,
  onSuccess
}) => {
  const { hideModal } = useModalData({ modalType: Modals.TWO_FACTOR_REGENERATE_CODES_MODAL })

  const [regenerateMutation, { loading }] = useMutation<IAuthTwoFactorRegenerateBackupCodesResponse>(
    AUTH_TWO_FACTOR_REGENERATE_CODES_MUTATION
  )

  const handleRegenerate = async (formData: FormData) => {
    try {
      const {
        data: {
          authTwoFactorRegenerateBackupCodes: { data }
        }
      } = await regenerateMutation({
        variables: {
          code: formData.code.trim()
        }
      })

      onSuccess(data.backupCodes)
    } catch (error) {
      handleError(error, 'TwoFactorRegenerateCodesModal.handleRegenerate')
    }
  }

  const onCancel = () => {
    reset()
    hideModal()
  }

  return (
    <FormContainer onSubmit={handleSubmit(handleRegenerate)}>
      <p>Enter your current TOTP code to generate new backup codes. Old codes will be invalidated.</p>

      <ReduxInput
        autoFocus
        type='text'
        name='code'
        autoComplete='off'
        validate={[required]}
        label='TOTP Code (6 digits)'
      />

      <ButtonGroup>
        <Button layout={BUTTON_LAYOUTS.WHITE} onClick={onCancel} type='button'>
          Cancel
        </Button>

        <Button layout={BUTTON_LAYOUTS.PURPLE} type='submit' disabled={loading} style={CREATE_BUTTON_STYLE}>
          {loading ? 'Regenerating...' : 'Regenerate Codes'}
        </Button>
      </ButtonGroup>
    </FormContainer>
  )
}

const TwoFactorRegenerateCodesFormWrapped = reduxForm<FormData, OwnProps>({
  form: Forms.TWO_FACTOR_REGENERATE_CODES_FORM
})(TwoFactorRegenerateCodesForm)

const TwoFactorRegenerateCodesModal: React.FC = () => {
  const { hideModal, modalData } = useModalData({ modalType: Modals.TWO_FACTOR_REGENERATE_CODES_MODAL })
  const { isOpen, refetch } = modalData

  const [newCodes, setNewCodes] = useState<string[] | null>(null)

  const handleSuccess = (codes: string[]) => {
    setNewCodes(codes)
    if (refetch) refetch()
  }

  const handleClose = () => {
    setNewCodes(null)
    hideModal()
  }

  return (
    <Modal width='md' isOpen={isOpen} hideModal={handleClose} PaperProps={PAPER_PROPS}>
      <Header>
        <span>Regenerate Backup Codes</span>
        <CloseIcon right={0} onClick={handleClose} />
      </Header>

      <ScrollableContent>
        {!newCodes ? (
          <TwoFactorRegenerateCodesFormWrapped onSuccess={handleSuccess} />
        ) : (
          <SuccessContainer>
            <SuccessTitle>New Backup Codes</SuccessTitle>
            <p>Save these new backup codes in a secure location. Your old codes have been invalidated.</p>

            <BackupCodesDisplay codes={newCodes} />

            <Button
              layout={BUTTON_LAYOUTS.PURPLE}
              onClick={handleClose}
              style={{ ...CREATE_BUTTON_STYLE, marginTop: '20px' }}>
              Done
            </Button>
          </SuccessContainer>
        )}
      </ScrollableContent>
    </Modal>
  )
}

export default TwoFactorRegenerateCodesModal
